import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import {
  ClipboardList, Plus, Loader2, Lock, User, Calendar, Clock, Eye,
  Copy, MessageCircle, Send, Mail, Trash2, CheckCircle2, ArrowRight, QrCode,
} from 'lucide-react';

export default function TeacherSheetsListPage() {
  const { user, loading: authLoading } = useAuth();
  const [sheets, setSheets] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [qrSheet, setQrSheet] = useState(null); // sheet object to show in QR modal

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'enseignant') return;
    (async () => {
      try { const r = await api.listSessionSheets(); setSheets(r.sheets || []); }
      catch { setSheets([]); }
    })();
  }, [authLoading, user]);

  if (authLoading) return <PageShell><div className="max-w-6xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user || user.role !== 'enseignant') {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto p-12 text-center">
          <Lock className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[28px]">Réservé aux enseignants ECSR</h2>
        </div>
      </PageShell>
    );
  }

  const buildUrl = (token) => `${window.location.origin}/fiche/${token}`;
  const buildShareText = (s) => `Salut ${s.student_name} !\n\nVoici la fiche de ta séance : ${buildUrl(s.token)}\n\n${user.name} (codevoiture.fr)`;

  const copyUrl = (token) => {
    navigator.clipboard.writeText(buildUrl(token));
    setCopiedToken(token); setTimeout(() => setCopiedToken(null), 1500);
  };
  const shareSMS = (s) => {
    const phone = (s.student_phone || '').replace(/\s/g, '');
    window.location.href = `sms:${phone}?body=${encodeURIComponent(buildShareText(s))}`;
  };
  const shareWA = (s) => {
    const phone = (s.student_phone || '').replace(/[^0-9+]/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildShareText(s))}`, '_blank');
  };
  const shareEmail = (s) => {
    const subj = encodeURIComponent(`Fiche de séance conduite · ${s.date}`);
    window.location.href = `mailto:?subject=${subj}&body=${encodeURIComponent(buildShareText(s))}`;
  };
  const del = async (id) => {
    if (!window.confirm('Supprimer cette fiche ? L\'élève ne pourra plus y accéder.')) return;
    try { await api.deleteSessionSheet(id); setSheets((prev) => prev.filter((x) => x.id !== id)); } catch {}
  };

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-8 sm:py-10">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/enseignant" className="hover:text-white">Dashboard</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Fiches de séance</span>
        </nav>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <SectionTitle kicker="Fiches de séance partageables" title="Mes fiches" sub="Chaque fiche est un lien unique envoyable par SMS, WhatsApp ou Email — l'élève y accède SANS créer de compte." />
          <Link to="/enseignant/fiche-seance/nouvelle" data-testid="sheets-new-btn" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold inline-flex items-center gap-2 shrink-0 shadow-[0_0_24px_-6px_rgba(239,68,68,0.6)]">
            <Plus size={14} /> Nouvelle fiche
          </Link>
        </div>

        <div className="mt-8">
          {sheets === null ? (
            <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
          ) : sheets.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-12 text-center" data-testid="sheets-empty">
              <ClipboardList size={36} className="mx-auto text-white/30 mb-4" />
              <h3 className="font-serif-editorial text-white text-[24px]">Aucune fiche pour l'instant</h3>
              <p className="text-white/55 text-[14px] mt-2 max-w-md mx-auto">Après chaque cours de conduite, crée une fiche récapitulative et envoie-la à ton élève par SMS ou WhatsApp — même s'il n'a pas encore de compte.</p>
              <Link to="/enseignant/fiche-seance/nouvelle" className="inline-block mt-6 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold">
                Créer ma première fiche
              </Link>
            </div>
          ) : (
            <div className="grid gap-3" data-testid="sheets-list">
              {sheets.map((s) => (
                <div key={s.id} className="rounded-xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5" data-testid={`sheet-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[14px] font-bold shrink-0">
                        {s.student_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-serif-editorial text-white text-[17px] truncate">{s.student_name}</div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-white/50 font-mono-spec">
                          {s.date && <span className="inline-flex items-center gap-1"><Calendar size={10} /> {s.date}</span>}
                          {s.duration_min && <span className="inline-flex items-center gap-1"><Clock size={10} /> {s.duration_min} min</span>}
                          {s.competences?.length > 0 && <span>REMC : {s.competences.join(' · ')}</span>}
                          <span className="inline-flex items-center gap-1"><Eye size={10} /> {s.view_count || 0} vue{(s.view_count || 0) > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button onClick={() => setQrSheet(s)} title="Afficher QR code" data-testid={`sheet-qr-${s.id}`} className="h-8 px-2.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[11.5px] font-bold inline-flex items-center gap-1 shadow-[0_0_16px_-4px_rgba(239,68,68,0.5)]">
                        <QrCode size={11} /> QR
                      </button>
                      <button onClick={() => copyUrl(s.token)} title="Copier le lien" className="h-8 px-2.5 rounded-full border border-white/15 text-white/70 hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        {copiedToken === s.token ? <CheckCircle2 size={11} className="text-emerald-400" /> : <Copy size={11} />}
                      </button>
                      <button onClick={() => shareSMS(s)} title="SMS" className="h-8 px-2.5 rounded-full bg-red-600/20 border border-red-500/40 text-red-200 hover:bg-red-600/30 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        <MessageCircle size={11} />
                      </button>
                      <button onClick={() => shareWA(s)} title="WhatsApp" className="h-8 px-2.5 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        <Send size={11} />
                      </button>
                      <button onClick={() => shareEmail(s)} title="Email" className="h-8 px-2.5 rounded-full bg-sky-600/20 border border-sky-500/40 text-sky-200 hover:bg-sky-600/30 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        <Mail size={11} />
                      </button>
                      <Link to={`/fiche/${s.token}`} target="_blank" title="Aperçu" className="h-8 px-2.5 rounded-full border border-white/15 text-white/70 hover:border-white/35 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        <ArrowRight size={11} />
                      </Link>
                      <button onClick={() => del(s.id)} title="Supprimer" className="h-8 px-2.5 rounded-full border border-red-500/30 text-red-300 hover:bg-red-950/40 text-[11.5px] font-semibold inline-flex items-center gap-1">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* QR Modal — displays the fiche QR for scanning */}
        {qrSheet && (
          <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setQrSheet(null)} data-testid="sheet-qr-modal">
            <div className="max-w-md w-full rounded-2xl border border-emerald-500/40 bg-[#0d0d0e] p-7 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="text-emerald-400 text-[11px] uppercase tracking-[0.22em] font-mono-spec font-bold">Fiche de séance · {qrSheet.date}</div>
              <h2 className="mt-2 font-serif-editorial text-white text-[24px] leading-tight">Fais scanner à {qrSheet.student_name}</h2>
              <div className="mt-5 flex justify-center">
                <div className="bg-white p-5 rounded-2xl">
                  <QRCodeSVG value={`${window.location.origin}/fiche/${qrSheet.token}`} size={280} level="H" />
                </div>
              </div>
              <p className="mt-4 text-white/60 text-[12.5px] leading-relaxed">Ton élève ouvre l'appareil photo de son téléphone, cadre le QR code, tape sur la notification → il/elle voit sa fiche du jour instantanément (aucun compte requis).</p>
              <div className="mt-4 text-white/40 text-[10.5px] font-mono-spec break-all">{window.location.origin}/fiche/{qrSheet.token}</div>
              <button onClick={() => setQrSheet(null)} className="mt-5 h-11 w-full rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Fermer</button>
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}
